import { EditorCaret, EditorLine, createLineId, emptyEditorLine } from './descriptionEditorCommands'
import { normalizeRuns, TextRun } from './mobileDescription'

const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const runsToHtml = (runs: TextRun[]): string => {
  const html = normalizeRuns(runs)
    .filter((run) => run.text.length > 0)
    .map((run) => (run.bold ? `<b>${escapeHtml(run.text)}</b>` : escapeHtml(run.text)))
    .join('')
  if (html.length === 0) {
    return '<br>'
  }
  return html
}

const isBoldElement = (element: HTMLElement): boolean => {
  const weight = element.style.fontWeight
  return element.tagName === 'B' || element.tagName === 'STRONG' || weight === 'bold' || Number(weight) >= 600
}

export const domToRuns = (root: HTMLElement): TextRun[] => {
  const runs: TextRun[] = []
  const walk = (node: Node, bold: boolean): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\n/g, '')
      if (text.length > 0) {
        runs.push({ text, bold })
      }
      return
    }
    if (!(node instanceof HTMLElement) || node.tagName === 'BR') {
      return
    }
    node.childNodes.forEach((child) => {
      walk(child, bold || isBoldElement(node))
    })
  }
  root.childNodes.forEach((child) => {
    walk(child, false)
  })
  return normalizeRuns(runs)
}

export const caretOffsetIn = (root: HTMLElement): number | null => {
  const selection = window.getSelection()
  if (selection === null || selection.rangeCount === 0) {
    return null
  }
  const range = selection.getRangeAt(0)
  if (!root.contains(range.startContainer)) {
    return null
  }
  const before = range.cloneRange()
  before.selectNodeContents(root)
  before.setEnd(range.startContainer, range.startOffset)
  return before.toString().length
}

export const placeCaret = (root: HTMLElement, offset: number): void => {
  const selection = window.getSelection()
  if (selection === null) {
    return
  }
  const range = document.createRange()
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let remaining = offset
  let node = walker.nextNode()
  while (node !== null) {
    const length = node.textContent?.length ?? 0
    if (remaining <= length) {
      range.setStart(node, remaining)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      return
    }
    remaining -= length
    node = walker.nextNode()
  }
  range.selectNodeContents(root)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

const textLineClass = 'mb-1 min-h-5 whitespace-pre-wrap break-words outline-none select-text'
const bulletLineClass = `${textLineClass} relative pl-4 before:pointer-events-none before:absolute before:left-0`
const ruleLineClass = 'my-2 flex h-8 items-center outline-none'

const lineRows = (root: HTMLElement): HTMLElement[] =>
  [...root.children].filter((node): node is HTMLElement => node instanceof HTMLElement && node.tagName === 'DIV')

const kindOf = (value: string): EditorLine['kind'] => {
  if (value === 'bullet' || value === 'rule') {
    return value
  }
  return 'text'
}

const readRow = (row: HTMLElement): EditorLine => {
  const { lineId, lineKind, marker } = row.dataset
  const kind = kindOf(lineKind ?? '')
  return {
    id: lineId ?? createLineId(),
    kind,
    marker: marker === '-' ? '-' : '*',
    runs: kind === 'rule' ? [{ text: '', bold: false }] : domToRuns(row)
  }
}

const paintRow = (row: HTMLElement, line: EditorLine): void => {
  row.dataset.lineId = line.id
  row.dataset.lineKind = line.kind
  row.dataset.marker = line.marker
  if (line.kind === 'rule') {
    row.className = ruleLineClass
    return
  }
  row.className = line.kind === 'bullet' ? bulletLineClass : textLineClass
}

const fillRow = (row: HTMLDivElement, line: EditorLine): void => {
  paintRow(row, line)
  if (line.kind === 'rule') {
    row.contentEditable = 'false'
    row.tabIndex = 0
    const bar = document.createElement('div')
    bar.className = 'h-0.5 w-full rounded-full bg-foregrounds-fg-base'
    row.append(bar)
    return
  }
  row.innerHTML = runsToHtml(line.runs)
}

export const writeEditorDom = (root: HTMLElement, lines: EditorLine[]): void => {
  root.replaceChildren()
  lines.forEach((line) => {
    const row = document.createElement('div')
    fillRow(row, line)
    root.append(row)
  })
}

export const readEditorDom = (root: HTMLElement): EditorLine[] => {
  const rows = lineRows(root).map(readRow)
  if (rows.length === 0) {
    return [emptyEditorLine()]
  }
  return rows
}

export const editorDomMatches = (root: HTMLElement, lines: EditorLine[]): boolean => {
  const rows = lineRows(root)
  if (rows.length !== lines.length) {
    return false
  }
  return lines.every((line, index) => {
    const row = rows[index]
    return row.dataset.lineId === line.id && row.dataset.lineKind === line.kind
  })
}

export const paintLines = (root: HTMLElement, lines: EditorLine[]): boolean => {
  const rows = lines.map((line) => root.querySelector<HTMLElement>(`[data-line-id="${CSS.escape(line.id)}"]`))
  if (rows.some((row) => row === null)) {
    return false
  }
  lines.forEach((line, index) => {
    const row = rows[index]
    if (row === null) {
      return
    }
    paintRow(row, line)
  })
  return true
}

export const lineRowFromNode = (root: HTMLElement, node: Node | null): HTMLElement | null => {
  if (node === null || !root.contains(node)) {
    return null
  }
  const element = node instanceof HTMLElement ? node : node.parentElement
  const row = element?.closest<HTMLElement>('[data-line-id]') ?? null
  if (row === null || !root.contains(row)) {
    return null
  }
  return row
}

export const caretFromSelection = (root: HTMLElement): EditorCaret | null => {
  const selection = window.getSelection()
  if (selection === null) {
    return null
  }
  const row = lineRowFromNode(root, selection.anchorNode)
  const id = row?.dataset.lineId ?? ''
  if (row === null || id === '') {
    return null
  }
  const offset = caretOffsetIn(row)
  if (offset === null) {
    return null
  }
  return { id, offset }
}

const selectionInRoot = (root: HTMLElement, selection: Selection): boolean =>
  selection.rangeCount > 0 && selection.anchorNode !== null && root.contains(selection.anchorNode)

export const affectedLineIds = (root: HTMLElement): string[] => {
  const selection = window.getSelection()
  if (selection === null || !selectionInRoot(root, selection)) {
    return []
  }
  if (selection.isCollapsed) {
    const id = lineRowFromNode(root, selection.anchorNode)?.dataset.lineId ?? ''
    return id === '' ? [] : [id]
  }
  const ids: string[] = []
  lineRows(root).forEach((row) => {
    const id = row.dataset.lineId ?? ''
    if (id !== '' && selection.containsNode(row, true)) {
      ids.push(id)
    }
  })
  return ids
}
